<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 pb-4">
      <SimplePageHeader title="Invoices" class="px-4 pr-5 mb-3" />
      <v-row class="px-5">
        <v-col cols="12" sm="6" md="4">
          <ClientsDropDownList
            :id="clientId"
            clearable
            dense
            hide-details
            @clientIdChange="clientId = $event"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="postingStateId"
            :items="postingStateOptions"
            item-text="name"
            item-value="id"
            label="All States"
            clearable
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="12" md="5">
          <DateRange
            :dateBegin="submittedFrom"
            :dateEnd="submittedTo"
            @dateBeginChange="submittedFrom = $event"
            @dateEndChange="submittedTo = $event"
          />
        </v-col>
      </v-row>
      <v-row class="px-5">
        <v-col cols="12" class="d-flex align-center">
          <v-btn color="primary" outlined @click="handleSearchClick">Search</v-btn>
          <v-btn class="ml-2" outlined @click="handleClearClick">Clear</v-btn>
        </v-col>
      </v-row>
    </div>

    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="invoiceList"
          :server-items-length="totalRecords"
          :options.sync="options"
          :footer-props="footerProps"
          class="elevation-2"
        >
          <template v-slot:item.weekEnding="{ item }">
            {{ item.weekEnding ? $options.filters.formatDate(item.weekEnding) : '' }}
          </template>
          <template v-slot:item.vmsSubmitDate="{ item }">
            {{ item.vmsSubmitDate | formatDate }}
          </template>
          <template v-slot:item.totalAmount="{ item }">
            {{ item.totalAmount | currency }}
          </template>
          <template v-slot:item.postingStateName="{ item }">
            <v-chip
              x-small
              label
              :color="postingStateAttrs(item.postingStateId).color"
              :dark="postingStateAttrs(item.postingStateId).dark"
            >{{ item.postingStateName }}</v-chip>
            <div v-if="failureReason(item)" class="caption red--text text-left">{{ failureReason(item) }}</div>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              v-if="canPost(item)"
              class="mx-1 my-1"
              color="primary"
              outlined
              title="Post to QuickBooks"
              @click.prevent="postFromList(item)"
            >
              <v-icon color="primary">cloud_upload</v-icon>
            </v-btn>
            <v-btn
              v-if="canRepost(item)"
              class="mx-1 my-1"
              color="primary"
              outlined
              title="Re-post to QuickBooks"
              @click.prevent="repostFromList(item)"
            >
              <v-icon color="primary">sync</v-icon>
            </v-btn>
            <v-btn
              v-if="item.quickBooksInvoiceUrl"
              class="mx-1 my-1"
              color="primary"
              outlined
              title="Open in QuickBooks"
              :href="item.quickBooksInvoiceUrl"
              target="_blank"
              rel="noopener"
            >
              <v-icon color="primary">open_in_new</v-icon>
            </v-btn>
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="viewInvoice($event, item)"
            >
              <v-icon color="primary">visibility</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { ClientsDropDownList } from '@/components/clients'
import { DateRange } from '@/components/shared/datePickers'
import { invoiceDetailRoute } from '@/lib/constants/routes'
import { INVOICE_POSTING_STATE_OPTIONS, invoicePostingStateAttrs } from '@/lib/constants/invoices'
import { canPostInvoice, canRepostInvoice } from './invoiceHelpers'
import { failureReason } from '@/lib/invoices'

export default {
  components: {
    BottomSpacer,
    SimplePageHeader,
    ClientsDropDownList,
    DateRange
  },
  data () {
    return {
      postingStateOptions: INVOICE_POSTING_STATE_OPTIONS,
      options: { page: 1, itemsPerPage: 25 },
      footerProps: { 'items-per-page-options': [10, 25, 50, 100] },
      headers: [
        { text: 'Client', value: 'clientName', sortable: false, width: '14%' },
        { text: 'VMS Invoice Id', value: 'vmsInvoiceId', sortable: false, width: '14%' },
        { text: 'Kind', value: 'kindName', sortable: false, width: '10%' },
        { text: 'Worker', value: 'workerName', sortable: false, width: '15%' },
        { text: 'Week Ending', value: 'weekEnding', align: 'center', sortable: false, width: '10%' },
        { text: 'Submitted', value: 'vmsSubmitDate', align: 'center', sortable: false, width: '10%' },
        { text: 'Amount', value: 'totalAmount', align: 'end', sortable: false, width: '10%' },
        { text: 'State', value: 'postingStateName', align: 'center', sortable: false, width: '12%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '5%' }
      ]
    }
  },
  computed: {
    ...mapFields('invoices', ['invoiceList', 'totalRecords']),
    ...mapFields('invoiceFilters', ['clientId', 'postingStateId', 'submittedFrom', 'submittedTo'])
  },
  watch: {
    options () {
      this.fetchInvoices()
    }
  },
  methods: {
    ...mapActions('invoices', ['getInvoicesWithFilters']),
    ...mapActions('invoiceFilters', ['clearFilters']),
    ...mapActions('invoice', ['postInvoice', 'repostInvoice']),
    postingStateAttrs (stateId) {
      return invoicePostingStateAttrs(stateId)
    },
    canPost (invoice) {
      return canPostInvoice(invoice)
    },
    canRepost (invoice) {
      return canRepostInvoice(invoice)
    },
    failureReason (invoice) {
      return failureReason(invoice)
    },
    postFromList (invoice) {
      return this.postInvoice({ id: invoice.id }).then(() => this.fetchInvoices())
    },
    repostFromList (invoice) {
      return this.repostInvoice({ id: invoice.id }).then(() => this.fetchInvoices())
    },
    fetchInvoices () {
      return this.getInvoicesWithFilters({ page: this.options.page, pageSize: this.options.itemsPerPage })
    },
    resetAndFetch () {
      if (this.options.page !== 1) {
        this.options = { ...this.options, page: 1 }
      } else {
        this.fetchInvoices()
      }
    },
    handleSearchClick () {
      this.resetAndFetch()
    },
    handleClearClick () {
      this.clearFilters()
      this.resetAndFetch()
    },
    viewInvoice (event, invoice) {
      const target = { name: invoiceDetailRoute.name, params: { id: invoice.id } }
      if (event.ctrlKey === true) {
        window.open(this.$router.resolve(target).href, '_blank')
      } else {
        this.$router.push(target)
      }
    },
    initialize (returningFromInvoice) {
      if (!returningFromInvoice) {
        this.clearFilters()
      }
      this.fetchInvoices()
    }
  },
  beforeRouteEnter (to, from, next) {
    const returningFromInvoice = from.name === invoiceDetailRoute.name
    next(vm => vm.initialize(returningFromInvoice))
  }
}
</script>
